// 工具函数入口文件
import DOMPurify from 'dompurify'
import type { Config } from 'dompurify'

// 导出日志工具
export { logger } from './logger'
import { logger } from './logger'

// 导出公共格式化工具
export {
  formatFileSize,
  formatDateTime,
  formatDateTimeShort,
  formatRelativeTime,
  formatCreator,
  formatDuration,
  getFileNameWithoutExt,
  isImageFile,
  isVideoFile
} from './format'

// 导出加密存储工具
export { secureStorage, tokenStorage } from './crypto-storage'

// 导出 Token 持有器（用于 API 同步获取 Token）
export { tokenHolder } from './token-holder'
export {
  getProjectId,
  getUserDisplayName,
  getUserId,
  isValidProjectId,
  normalizeUserDisplayName,
  resolveProjectId,
  resolveUserId,
  setProjectId,
  sweepInvalidIdentityStorage,
} from './identity'

// 导出并发控制工具
export { runBatch, allSettledWithLimit, sleep } from './concurrency'
export type { BatchResult, ConcurrencyOptions } from './concurrency'

/**
 * DOMPurify 预设配置
 */
const SANITIZE_PRESETS = {
  /**
   * 基础配置 - 仅允许简单文本格式
   */
  basic: {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'span'],
    ALLOWED_ATTR: ['href', 'class', 'target', 'rel'],
  },
  /**
   * Markdown 渲染配置 - 支持完整的 markdown 转换结果
   */
  markdown: {
    ALLOWED_TAGS: [
      // 文本格式
      'b', 'i', 'em', 'strong', 'u', 's', 'del', 'ins', 'mark', 'sub', 'sup',
      // 段落和布局
      'p', 'br', 'hr', 'span', 'div', 'section', 'article',
      // 标题
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      // 列表
      'ul', 'ol', 'li', 'dl', 'dt', 'dd',
      // 代码
      'code', 'pre', 'kbd', 'samp', 'var',
      // 引用
      'blockquote', 'q', 'cite',
      // 表格
      'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'colgroup', 'col',
      // 链接和媒体
      'a', 'img', 'figure', 'figcaption',
      // 其他
      'details', 'summary', 'abbr', 'address', 'time',
    ],
    ALLOWED_ATTR: [
      'href', 'src', 'alt', 'title', 'class', 'id', 'name',
      'target', 'rel', 'width', 'height',
      'colspan', 'rowspan', 'scope', 'headers',
      'datetime', 'lang', 'dir', 'open',
      // 代码高亮相关
      'data-language', 'data-line',
    ],
    // 安全设置
    ALLOW_DATA_ATTR: false,
    ADD_ATTR: ['target'],
    FORBID_TAGS: ['script', 'style', 'iframe', 'form', 'input', 'button', 'object', 'embed'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
  },
  /**
   * 严格配置 - 仅纯文本
   */
  strict: {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  },
} as const

type SanitizePreset = keyof typeof SANITIZE_PRESETS

/**
 * 安全地处理 HTML 内容，防止 XSS 攻击
 *
 * @param html - 原始 HTML 字符串
 * @param options - DOMPurify 配置选项或预设名称
 * @returns 清理后的安全 HTML 字符串
 *
 * @example
 * ```typescript
 * // 使用 markdown 预设（推荐用于渲染 AI 响应）
 * const safeHtml = sanitizeHtml(markdownToHtml(content), 'markdown')
 *
 * // 使用基础预设
 * const safeHtml = sanitizeHtml(userInput, 'basic')
 *
 * // 自定义配置
 * const safeHtml = sanitizeHtml(content, { ALLOWED_TAGS: ['p', 'br'] })
 * ```
 */
export function sanitizeHtml(html: string, options?: Config | SanitizePreset): string {
  if (!html) return ''

  // 如果传入预设名称，使用预设配置
  if (typeof options === 'string' && options in SANITIZE_PRESETS) {
    // 使用类型断言处理 readonly 数组
    return DOMPurify.sanitize(html, SANITIZE_PRESETS[options] as unknown as Config)
  }

  // 默认使用 markdown 预设（最常用场景）
  const config = options ?? SANITIZE_PRESETS.markdown
  return DOMPurify.sanitize(html, config as unknown as Config)
}

/**
 * 快捷方法：清理 Markdown 渲染的 HTML
 * 用于渲染 AI 响应、文档内容等
 */
export function sanitizeMarkdownHtml(html: string): string {
  return sanitizeHtml(html, 'markdown')
}

/**
 * 快捷方法：清理用户输入的基础 HTML
 * 用于用户评论、留言等
 */
export function sanitizeBasicHtml(html: string): string {
  return sanitizeHtml(html, 'basic')
}

/**
 * 快捷方法：完全移除 HTML 标签，仅保留纯文本
 */
export function sanitizeToPlainText(html: string): string {
  return sanitizeHtml(html, 'strict')
}

/**
 * 兼容 HTTP 环境的剪贴板复制工具函数
 * 
 * 问题背景：
 * - navigator.clipboard API 只能在安全上下文（Secure Context）中使用
 * - localhost 和 HTTPS 站点被视为安全上下文
 * - HTTP 站点（非 localhost）不是安全上下文，会导致 clipboard API 不可用
 * 
 * 解决方案：
 * - 优先使用现代的 navigator.clipboard.writeText() API
 * - 如果不可用或失败，回退到传统的 document.execCommand('copy') 方法
 * 
 * @param text - 要复制到剪贴板的文本
 * @returns Promise<boolean> - 复制是否成功
 * 
 * @example
 * ```typescript
 * import { copyToClipboard } from '@/utils'
 * 
 * const success = await copyToClipboard('Hello, World!')
 * if (success) {
 *   Message.success('已复制到剪贴板')
 * } else {
 *   Message.error('复制失败，请手动复制')
 * }
 * ```
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  // 方法1：尝试使用现代 Clipboard API（仅在安全上下文可用）
  if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
    try {
      await navigator.clipboard.writeText(text)
      return true
    } catch (err) {
      // Clipboard API 失败，继续尝试传统方法
      logger.warn('[Clipboard] navigator.clipboard.writeText failed:', err)
    }
  }

  // 方法2：回退到传统的 execCommand 方法（兼容 HTTP 环境）
  try {
    // 创建临时 textarea 元素
    const textArea = document.createElement('textarea')
    textArea.value = text
    
    // 设置样式，确保不可见且不影响页面布局
    textArea.style.position = 'fixed'
    textArea.style.left = '-9999px'
    textArea.style.top = '-9999px'
    textArea.style.width = '1px'
    textArea.style.height = '1px'
    textArea.style.padding = '0'
    textArea.style.border = 'none'
    textArea.style.outline = 'none'
    textArea.style.boxShadow = 'none'
    textArea.style.background = 'transparent'
    // 防止 iOS 缩放
    textArea.style.fontSize = '16px'
    // 设置只读，防止键盘弹出（移动端）
    textArea.setAttribute('readonly', '')
    
    document.body.appendChild(textArea)
    
    // 选中文本
    textArea.select()
    textArea.setSelectionRange(0, text.length)
    
    // 执行复制命令
    const success = document.execCommand('copy')
    
    // 清理
    document.body.removeChild(textArea)
    
    return success
  } catch (err) {
    logger.error('[Clipboard] execCommand fallback failed:', err)
    return false
  }
}

/**
 * 生成 UUID v4
 * 优先使用 crypto.randomUUID()，非安全上下文（HTTP）下回退到 Math.random 实现
 */
export function generateUUID(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch {
    // 非安全上下文（HTTP）下 crypto.randomUUID 可能存在但调用会抛出 TypeError
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/**
 * 格式化日期
 */
export function formatDate(date: Date | string | number, format = 'YYYY-MM-DD HH:mm:ss'): string {
  const d = new Date(date)
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const hours = String(d.getHours()).padStart(2, '0')
  const minutes = String(d.getMinutes()).padStart(2, '0')
  const seconds = String(d.getSeconds()).padStart(2, '0')

  return format
    .replace('YYYY', String(year))
    .replace('MM', month)
    .replace('DD', day)
    .replace('HH', hours)
    .replace('mm', minutes)
    .replace('ss', seconds)
}

/**
 * 防抖函数
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null

  return function (this: unknown, ...args: Parameters<T>) {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => {
      func.apply(this, args)
    }, wait)
  }
}

/**
 * 节流函数
 */
export function throttle<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let lastTime = 0

  return function (this: unknown, ...args: Parameters<T>) {
    const now = Date.now()
    if (now - lastTime >= wait) {
      lastTime = now
      func.apply(this, args)
    }
  }
}

/**
 * 解析流式字符串输出，提取三个 XML 标签的内容
 * 
 * 处理 LLM 的流式输出，实时解析出：
 * - <thought>...</thought> 思考过程
 * - <response>...</response> 最终回答
 * - <suggestions>...</suggestions> 建议问题（JSON 数组）
 * 
 * 特性：
 * - 支持标签未闭合的情况（流式传输中）
 * - 自动处理标签顺序问题
 * - 健壮的 JSON 解析，处理不完整的 JSON
 * - 如果同一标签出现多次，取最后一个
 * 
 * @param content - 不断增长的完整字符串内容
 * @returns 解析结果对象，包含三个部分
 * 
 * @example
 * ```typescript
 * // 完整标签
 * const result1 = parseStreamPart('<thought>深度思考中...</thought><response>根据查询结果...</response><suggestions>["问题1", "问题2"]</suggestions>')
 * // { thought: "深度思考中...", response: "根据查询结果...", suggestions: ["问题1", "问题2"] }
 *
 * // 未闭合标签（流式传输中）
 * const result2 = parseStreamPart('<thought>深度思考中...<response>根据查询结果...<suggestions>["问题1"')
 * // { thought: "深度思考中...", response: "根据查询结果...", suggestions: [] }
 * ```
 */
export function parseStreamPart(content: string): {
  thought: string
  response: string
  suggestions: string[]
} {
  const result = {
    thought: '',
    response: '',
    suggestions: [] as string[]
  }

  if (!content || typeof content !== 'string') {
    return result
  }

  /**
   * 提取标签内容的辅助函数
   * @param tagName - 标签名称（不含尖括号）
   * @returns 标签内容，如果标签未闭合则返回到字符串末尾的内容
   */
  function extractTagContent(tagName: string): string {
    const openTag = `<${tagName}>`
    const closeTag = `</${tagName}>`
    
    // 查找所有出现的位置（处理标签可能重复的情况）
    const openTagIndices: number[] = []
    let searchIndex = 0
    while (true) {
      const index = content.indexOf(openTag, searchIndex)
      if (index === -1) break
      openTagIndices.push(index)
      searchIndex = index + openTag.length
    }

    if (openTagIndices.length === 0) {
      return ''
    }

    // 取最后一个开始标签的位置（处理标签重复的情况）
    const lastOpenIndex = openTagIndices[openTagIndices.length - 1]
    const startPos = lastOpenIndex + openTag.length
    
    // 查找对应的闭合标签
    const closeIndex = content.indexOf(closeTag, startPos)
    
    if (closeIndex !== -1) {
      // 标签已闭合
      return content.substring(startPos, closeIndex).trim()
    } else {
      // 标签未闭合，提取到字符串末尾
      // 但要排除后续可能出现的其他标签的开始
      const otherTags = ['<thought>', '<response>', '<suggestions>'].filter(t => t !== openTag)
      let endPos = content.length
      
      for (const otherTag of otherTags) {
        const otherTagIndex = content.indexOf(otherTag, startPos)
        if (otherTagIndex !== -1 && otherTagIndex < endPos) {
          endPos = otherTagIndex
        }
      }
      
      return content.substring(startPos, endPos).trim()
    }
  }

  // 提取 thought 内容
  result.thought = extractTagContent('thought')

  // 提取 response 内容
  result.response = extractTagContent('response')

  // 提取 suggestions 内容并解析为 JSON 数组
  const suggestionsContent = extractTagContent('suggestions')
  
  if (suggestionsContent) {
    try {
      // 尝试直接解析
      const parsed = JSON.parse(suggestionsContent.trim())
      if (Array.isArray(parsed)) {
        result.suggestions = parsed.filter(item => typeof item === 'string')
      }
    } catch {
      // JSON 不完整，尝试修复常见的未闭合情况
      try {
        let fixedContent = suggestionsContent.trim()
        
        // 如果以 [ 开头但没有 ]，尝试补全
        if (fixedContent.startsWith('[')) {
          // 检查是否有未闭合的字符串
          let inString = false
          let escapeNext = false
          let bracketCount = 0
          
          for (let i = 0; i < fixedContent.length; i++) {
            const char = fixedContent[i]
            
            if (escapeNext) {
              escapeNext = false
              continue
            }
            
            if (char === '\\') {
              escapeNext = true
              continue
            }
            
            if (char === '"') {
              inString = !inString
            } else if (!inString) {
              if (char === '[') bracketCount++
              if (char === ']') bracketCount--
            }
          }
          
          // 如果字符串未闭合，补引号
          if (inString) {
            fixedContent += '"'
          }
          
          // 如果括号未闭合，补闭合括号
          if (bracketCount > 0) {
            // 检查最后一个字符，如果是逗号，先移除
            if (fixedContent.endsWith(',')) {
              fixedContent = fixedContent.slice(0, -1)
            }
            fixedContent += ']'
          }
        }
        
        const parsed = JSON.parse(fixedContent)
        if (Array.isArray(parsed)) {
          result.suggestions = parsed.filter(item => typeof item === 'string')
        }
      } catch {
        // 如果还是解析失败，返回空数组
        result.suggestions = []
      }
    }
  }

  return result
}
